import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';
import { Camera, Upload, CheckCircle2, Trash2 } from 'lucide-react';
import ProgressPhotoImage from './ProgressPhotoImage';
import { resizeImageFile } from '../../utils/resizeImageFile';
import { PROGRESS_PHOTO_SLOTS, PROGRESS_PHOTO_SLOT_PHASES, normalizePhotoSlot } from '../../constants/progressPhotoSlots';

export default function ProgressPhotosPanel({
  variant = 'gallery',
  compact = false,
  readOnly = false,
  apiBase = '/d28d/progress-photos',
  mediaApiBase = apiBase,
  className = '',
}) {
  const { t } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [cycleMeta, setCycleMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState('');
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const slotInputRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [api.get(apiBase)];
      if (!readOnly && variant === 'slots') {
        requests.push(api.get('/d28d/progress/me').catch(() => null));
      }
      const [res, progressRes] = await Promise.all(requests);
      setPhotos(res.data?.data || []);
      const progress = progressRes?.data?.data;
      if (progress) {
        setCycleMeta({
          cycle_ended: progress.cycle_ended === true,
          cycle_name: progress.cycle_name,
          premiacion_enrolled: progress.premiacion_enrolled,
          premiacion_name: progress.premiacion_name,
        });
      } else {
        setCycleMeta(null);
      }
    } catch {
      setPhotos([]);
      setCycleMeta(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, readOnly, variant]);

  useEffect(() => { load(); }, [load]);

  const photosBySlot = useMemo(() => {
    const map = new Map();
    photos.forEach((p) => {
      const key = normalizePhotoSlot(p.slot) || p.slot;
      if (key) map.set(String(key), p);
    });
    return map;
  }, [photos]);

  const filledCount = useMemo(
    () => PROGRESS_PHOTO_SLOTS.filter(({ slot }) => photosBySlot.has(slot)).length,
    [photosBySlot],
  );

  const upload = async (file, slot = null) => {
    if (!file) return;
    const prepared = await resizeImageFile(file);
    const form = new FormData();
    form.append('file', prepared);
    if (caption.trim()) form.append('caption', caption.trim());
    if (slot) form.append('slot', slot);
    try {
      if (slot) setUploadingSlot(slot);
      await api.post(apiBase, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      if (slot && slotInputRefs.current[slot]) slotInputRefs.current[slot].value = '';
      emitToast({ type: 'success', title: t('progress_photos.saved', 'Foto guardada') });
      load();
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      emitToast({
        type: 'error',
        title: t('progress_photos.error', 'Error'),
        message: status === 413
          ? (serverMsg || t('progress_photos.too_large', 'La imagen es demasiado pesada. Usa una foto de hasta 15 MB o comprímela.'))
          : (serverMsg || t('progress_photos.upload_fail', 'No se pudo subir')),
      });
      if (err.response?.data?.meta) {
        setCycleMeta((prev) => ({ ...(prev || {}), ...err.response.data.meta }));
      }
    } finally {
      setUploadingSlot(null);
    }
  };

  const removePhoto = async (photo) => {
    if (!photo?.id || readOnly) return;
    const confirmMsg = t('progress_photos.delete_confirm', '¿Eliminar esta foto? Podrás subir otra en su lugar.');
    if (!window.confirm(confirmMsg)) return;
    try {
      setDeletingId(photo.id);
      await api.delete(`${apiBase}/${photo.id}`);
      emitToast({ type: 'success', title: t('progress_photos.deleted', 'Foto eliminada') });
      load();
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('progress_photos.error', 'Error'),
        message: err.response?.data?.error || t('progress_photos.delete_fail', 'No se pudo eliminar'),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const openLightbox = (photo, alt) => {
    if (!photo?.url) return;
    setLightbox({ src: photo.url, alt: alt || photo.caption || t('progress_photos.default_alt', 'Progreso') });
  };

  const openSlotPicker = (slot) => {
    if (uploadingSlot || cycleMeta?.cycle_ended) return;
    slotInputRefs.current[slot]?.click();
  };

  if (loading) {
    return <p className="d28d-text-muted text-sm">{t('progress_photos.loading', 'Cargando fotos…')}</p>;
  }

  if (variant === 'slots') {
    const cycleClosed = cycleMeta?.cycle_ended === true;
    const premiacionEnrolled = cycleMeta?.premiacion_enrolled === true;
    return (
      <div className={`progress-photos-slots space-y-4 ${className}`.trim()}>
        {!readOnly && cycleClosed ? (
          <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-[var(--d28d-text)]">
            <p className="font-semibold">{t('progress_photos.cycle_closed_title', 'Ciclo cerrado')}</p>
            <p className="mt-1 opacity-90">
              {t('progress_photos.cycle_closed_hint', 'El ciclo actual ya terminó. No puedes subir nuevas fotos hasta el próximo ciclo.')}
            </p>
          </div>
        ) : null}
        {!readOnly && cycleMeta?.premiacion_name ? (
          <div className={`rounded-xl border p-3 text-sm ${premiacionEnrolled ? 'border-lime-400/50 bg-lime-500/10' : 'border-stone-400/40 bg-stone-500/5'}`}>
            <p className="font-semibold text-[var(--d28d-text)]">
              {premiacionEnrolled
                ? t('progress_photos.premiacion_enrolled', 'Inscrito en premiación')
                : t('progress_photos.premiacion_not_enrolled', 'No inscrito en premiación')}
            </p>
            <p className="mt-1 d28d-text-muted text-xs">
              {cycleMeta.premiacion_name}
            </p>
          </div>
        ) : null}
        {!compact && !readOnly ? (
          <p className="text-sm d28d-text-muted">
            {t('progress_photos.slots_hint_cycle', 'Sube 4 fotos al inicio del ciclo y 4 al final (frente, lateral derecha, espalda y lateral izquierdo).')}
          </p>
        ) : null}

        {!readOnly ? (
          <div className="flex items-center justify-between gap-3 text-xs d28d-text-muted">
            <span>
              {t('progress_photos.progress_count', '{count} de {total} fotos', {
                count: filledCount,
                total: PROGRESS_PHOTO_SLOTS.length,
              })}
            </span>
            <span>{t('progress_photos.tap_replace', 'Toca la foto para ampliarla o usa Cambiar / Borrar')}</span>
          </div>
        ) : (
          <div className="text-xs d28d-text-muted">
            {t('progress_photos.progress_count', '{count} de {total} fotos', {
              count: filledCount,
              total: PROGRESS_PHOTO_SLOTS.length,
            })}
          </div>
        )}

        {PROGRESS_PHOTO_SLOT_PHASES.map((phaseGroup) => (
          <div key={phaseGroup.phase} className="space-y-3">
            <h4 className="text-sm font-bold text-[var(--d28d-text)] uppercase tracking-wide">
              {t(phaseGroup.labelKey, phaseGroup.fallback)}
            </h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {phaseGroup.slots.map(({ slot, labelKey, fallback }) => {
                const existing = photosBySlot.get(slot);
                const busy = uploadingSlot === slot;
                return (
                  <div
                    key={slot}
                    className={`progress-photo-slot ${existing ? 'progress-photo-slot--filled' : ''}`}
                  >
                    <div className="progress-photo-slot__header">
                      <span className="progress-photo-slot__label">
                        {t(labelKey, fallback)}
                      </span>
                      {existing ? (
                        <CheckCircle2 size={15} className="text-emerald-400 shrink-0" aria-hidden />
                      ) : readOnly ? null : (
                        <Upload size={15} className="d28d-text-muted shrink-0" aria-hidden />
                      )}
                    </div>

                    {readOnly ? (
                      <div className="progress-photo-slot__frame progress-photo-slot__frame--static">
                        {existing ? (
                          <button
                            type="button"
                            className="progress-photo-slot__enlarge w-full h-full"
                            onClick={() => openLightbox(existing, t(labelKey, fallback))}
                            aria-label={t('progress_photos.enlarge', 'Ver foto ampliada')}
                          >
                            <ProgressPhotoImage
                              src={existing.url}
                              alt={existing.caption || t(labelKey, fallback)}
                              className="progress-photo-slot__img"
                              linkable={false}
                              mediaApiBase={mediaApiBase}
                            />
                          </button>
                        ) : (
                          <span className="progress-photo-slot__empty">
                            <Camera size={24} strokeWidth={1.5} />
                            <span>{t('progress_photos.pending', 'Pendiente')}</span>
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        {existing ? (
                          <button
                            type="button"
                            className="progress-photo-slot__frame progress-photo-slot__frame--static progress-photo-slot__enlarge cursor-zoom-in"
                            onClick={() => openLightbox(existing, t(labelKey, fallback))}
                            aria-label={t('progress_photos.enlarge', 'Ver foto ampliada')}
                          >
                            <ProgressPhotoImage
                              src={existing.url}
                              alt={existing.caption || t(labelKey, fallback)}
                              className="progress-photo-slot__img"
                              linkable={false}
                              mediaApiBase={mediaApiBase}
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="progress-photo-slot__frame"
                            onClick={() => openSlotPicker(slot)}
                            disabled={busy || cycleMeta?.cycle_ended}
                            aria-label={t('progress_photos.upload_slot', 'Subir foto de {slot}', { slot: t(labelKey, fallback) })}
                          >
                            <span className="progress-photo-slot__empty">
                              <Camera size={24} strokeWidth={1.5} />
                              <span>{busy ? t('progress_photos.uploading', 'Subiendo…') : t('progress_photos.tap_upload', 'Toca para subir')}</span>
                            </span>
                          </button>
                        )}

                        <input
                          ref={(el) => { slotInputRefs.current[slot] = el; }}
                          type="file"
                          accept="image/*"
                          tabIndex={-1}
                          aria-hidden
                          className="progress-photo-slot__input"
                          disabled={busy || cycleMeta?.cycle_ended}
                          onChange={(e) => {
                            upload(e.target.files?.[0], slot);
                            e.target.value = '';
                          }}
                        />
                      </>
                    )}

                    {existing?.recorded_at ? (
                      <time className="progress-photo-slot__date" dateTime={existing.recorded_at}>
                        {formatDate(existing.recorded_at)}
                      </time>
                    ) : (
                      <span className="progress-photo-slot__date progress-photo-slot__date--empty">
                        {t('progress_photos.pending', 'Pendiente')}
                      </span>
                    )}
                    {!readOnly && existing ? (
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          className="text-xs font-semibold px-2 py-1 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-100 flex-1"
                          onClick={() => openSlotPicker(slot)}
                          disabled={busy || cycleMeta?.cycle_ended}
                        >
                          {t('progress_photos.replace_short', 'Cambiar')}
                        </button>
                        <button
                          type="button"
                          className="text-xs font-semibold px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 flex-1 inline-flex items-center justify-center gap-1"
                          onClick={() => removePhoto(existing)}
                          disabled={deletingId === existing.id || cycleMeta?.cycle_ended}
                        >
                          <Trash2 size={12} aria-hidden />
                          {deletingId === existing.id
                            ? t('progress_photos.deleting', 'Borrando…')
                            : t('progress_photos.delete', 'Borrar')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {readOnly && filledCount === 0 ? (
          <p className="d28d-text-muted text-sm">{t('progress_photos.empty', 'Aún no tienes fotos de progreso.')}</p>
        ) : null}
        {lightbox ? (
          <PhotoLightbox
            photo={lightbox}
            mediaApiBase={mediaApiBase}
            onClose={() => setLightbox(null)}
            t={t}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`.trim()}>
      <p className="text-sm d28d-text-muted">
        {t('progress_photos.hint', 'Sube fotos de progreso (frente, perfil o detalle) para comparar tu evolución.')}
      </p>
      <div className="card p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <label className="flex-1 w-full text-sm">
          <span className="font-semibold block mb-1">{t('progress_photos.caption', 'Nota (opcional)')}</span>
          <input className="input w-full" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t('progress_photos.caption_ph', 'Ej. Semana 4 — frente')} />
        </label>
        <label className="btn-primary cursor-pointer inline-flex items-center gap-2 shrink-0">
          <Camera size={16} />
          {t('progress_photos.upload', 'Subir foto')}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      {photos.length === 0 ? (
        <p className="d28d-text-muted text-sm">{t('progress_photos.empty', 'Aún no tienes fotos de progreso.')}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p) => (
            <figure key={p.id} className="rounded-xl overflow-hidden border border-[var(--d28d-border)] bg-[var(--d28d-surface-3)]">
              <button
                type="button"
                className="progress-photo-slot__frame progress-photo-slot__frame--static progress-photo-slot__enlarge w-full cursor-zoom-in"
                onClick={() => openLightbox(p, p.caption || t('progress_photos.default_alt', 'Progreso'))}
                aria-label={t('progress_photos.enlarge', 'Ver foto ampliada')}
              >
                <ProgressPhotoImage src={p.url} alt={p.caption || t('progress_photos.default_alt', 'Progreso')} className="progress-photo-slot__img" linkable={false} mediaApiBase={mediaApiBase} />
              </button>
              <figcaption className="p-2 text-xs d28d-text-muted">
                {p.caption || formatDate(p.recorded_at)}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {lightbox ? (
        <PhotoLightbox
          photo={lightbox}
          mediaApiBase={mediaApiBase}
          onClose={() => setLightbox(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}

function PhotoLightbox({ photo, mediaApiBase, onClose, t }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-label={photo?.alt}
      onClick={onClose}
    >
      <div
        className="training-modal-panel surface-readable bg-[var(--d28d-surface)] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--d28d-border)] shrink-0">
          <h3 className="font-semibold text-[var(--d28d-text)] truncate pr-2">{photo?.alt}</h3>
          <button type="button" className="btn-secondary text-sm shrink-0" onClick={onClose}>
            {t('common.close', 'Cerrar')}
          </button>
        </div>
        <div className="p-4 flex items-center justify-center bg-black/40 min-h-[200px]">
          <ProgressPhotoImage
            src={photo?.src}
            alt={photo?.alt}
            className="max-w-full max-h-[75vh] w-auto h-auto object-contain rounded-lg"
            linkable={false}
            mediaApiBase={mediaApiBase}
          />
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return '';
  }
}
